import QRCode from 'qrcode';
import { Certificate, VerificationLog, AdminProfile } from '../types';

// Auto-detect environment to decide between Node API (for preview) and PHP API (for cPanel)
const isPreviewEnv = typeof window !== 'undefined' && (
  window.location.hostname === 'localhost' || 
  window.location.hostname.includes('run.app') || 
  window.location.hostname.includes('127.0.0.1')
);

const usePHP = !isPreviewEnv; // True in production cPanel, False in AI Studio preview

const API_BASE = window.location.pathname.includes('/expertise') ? '/expertise/api' : '/api';
const NODE_API_BASE = window.location.origin + '/api';

const getEndpoint = (route: string) => {
  if (usePHP) {
    if (route === 'certificates') return `${API_BASE}/certificates.php`;
    if (route === 'logs') return `${API_BASE}/logs.php`;
    if (route.startsWith('certificates/')) return `${API_BASE}/certificates.php?id=${encodeURIComponent(route.split('/')[1])}`;
    if (route.startsWith('verify/')) return `${API_BASE}/verify.php?id=${encodeURIComponent(route.split('/')[1])}`;
    if (route === 'auth/verify_password') return `${API_BASE}/auth.php?action=verify_password`;
    return `${API_BASE}/${route}.php`;
  }
  return `${NODE_API_BASE}/${route}`;
};

export async function generateCryptographicHash(cert: { id: string; recipientName: string; standardCode: string; issueDate: string; leadAuditor?: string }): Promise<string> {
  const dataString = `${cert.id}|${cert.recipientName}|${cert.standardCode}|${cert.issueDate}|${cert.leadAuditor || ''}|CHC_SECURE_SALT_2025`;
  if (window.crypto && window.crypto.subtle) {
    try {
      const msgBuffer = new TextEncoder().encode(dataString);
      const hashBuffer = await window.crypto.subtle.digest('SHA-256', msgBuffer);
      const hashArray = Array.from(new Uint8Array(hashBuffer));
      return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
    } catch {}
  }
  let hash = 0;
  for (let i = 0; i < dataString.length; i++) {
    const char = dataString.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash |= 0;
  }
  const hex = Math.abs(hash).toString(16).padStart(8, '0');
  return `sha256_${hex}${Date.now().toString(16)}`;
}

export async function generateQRCode(certId: string): Promise<string> {
  try {
    const origin = typeof window !== 'undefined' ? window.location.origin : 'https://www.cherifhaddad.com';
    const verifyUrl = `${origin}/#verification?cert=${encodeURIComponent(certId)}`;
    
    return await QRCode.toDataURL(verifyUrl, {
      width: 320,
      margin: 2,
      color: { dark: '#0A192F', light: '#FFFFFF' },
      errorCorrectionLevel: 'H',
    });
  } catch (err) {
    console.error('Error generating QR code:', err);
    return '';
  }
}

export async function getCertificates(): Promise<Certificate[]> {
  try {
    const res = await fetch(getEndpoint('certificates'));
    if (!res.ok) throw new Error('API failed');
    const data = await res.json();
    return Array.isArray(data) ? data : [];
  } catch (e) {
    console.warn('Failed to load certificates from DB', e);
    return [];
  }
}

export async function saveCertificate(newCert: Certificate): Promise<Certificate[]> {
  if (!newCert.certificateHash) {
    newCert.certificateHash = await generateCryptographicHash(newCert);
  }
  if (!newCert.qrCodeDataUrl) {
    newCert.qrCodeDataUrl = await generateQRCode(newCert.id);
  }
  
  await fetch(getEndpoint('certificates'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(newCert)
  });
  
  return await getCertificates();
}

export async function deleteCertificate(id: string): Promise<Certificate[]> {
  await fetch(getEndpoint(`certificates/${id}`), { method: 'DELETE' });
  return await getCertificates();
}

export async function updateCertificateStatus(id: string, status: 'active' | 'revoked' | 'expired'): Promise<Certificate[]> {
  await fetch(getEndpoint(`certificates/${id}`), {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(usePHP ? { id, status } : { status })
  });
  return await getCertificates();
}

export async function verifyCertificate(searchId: string): Promise<{
  certificate: Certificate | null;
  status: 'valid' | 'revoked' | 'expired' | 'not_found';
}> {
  try {
    const res = await fetch(getEndpoint(`verify/${searchId}`));
    return await res.json();
  } catch (e) {
    return { certificate: null, status: 'not_found' };
  }
}

export async function getVerificationLogs(): Promise<VerificationLog[]> {
  try {
    const res = await fetch(getEndpoint('logs'));
    if (!res.ok) throw new Error();
    const data = await res.json();
    return Array.isArray(data) ? data : [];
  } catch (e) {
    return [];
  }
}

export async function recordVerificationLog(log: VerificationLog): Promise<void> {
  // Handled automatically by backend verify endpoint
}

export async function resetToDefaultData(): Promise<Certificate[]> {
  return await getCertificates();
}

const DEFAULT_ADMIN_PROFILE = { name: 'Admin', role: 'System Administrator' };

export function getAdminProfile(): AdminProfile {
  return DEFAULT_ADMIN_PROFILE as AdminProfile;
}

export function saveAdminProfile(profile: Partial<AdminProfile>): AdminProfile {
  return { ...DEFAULT_ADMIN_PROFILE, ...profile } as AdminProfile;
}

export async function verifyAdminPassword(inputPassword: string): Promise<boolean> {
  try {
    const res = await fetch(getEndpoint('auth/verify_password'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ password: inputPassword })
    });
    const data = await res.json();
    return !!data.success;
  } catch (e) {
    return false;
  }
}

export function updateAdminPassword(oldPassword: string, newPassword: string): { success: boolean; error?: string } {
  return { success: false, error: 'Not implemented' };
}
